import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import NavBar from "@/components/NavBar";
import EditGameForm from "@/components/game-detail/EditGameForm";
import LoadingView from "@/components/scoring/LoadingView";
import { useGameData } from "@/hooks/useGameData";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Flag, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const GameEdit = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeRound, isLoading } = useGameData(gameId);

  const [creatorId, setCreatorId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEndGameDialogOpen, setIsEndGameDialogOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const game = activeRound?.eligibleGames?.[0];
  const isCreator = user?.id != null && user.id === creatorId;

  // Fetch the game's creator ID
  useEffect(() => {
    if (!gameId) return;
    supabase
      .from('games')
      .select('user_id')
      .eq('id', gameId)
      .single()
      .then(({ data }) => {
        if (data) setCreatorId(data.user_id);
      });
  }, [gameId]);

  const handleEndGame = async () => {
    if (!gameId) return;
    setIsActionLoading(true);

    try {
      const { error } = await supabase
        .from('games')
        .update({ end_date: new Date().toISOString() })
        .eq('id', gameId);

      if (error) throw error;

      setIsEndGameDialogOpen(false);
      toast({
        title: "Game Ended",
        description: "This year's game has been marked as completed."
      });
      navigate("/games");
    } catch (error) {
      console.error("Error ending game:", error);
      toast({
        title: "Error",
        description: "Failed to end the game. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!gameId) return;
    setIsActionLoading(true);

    try {
      // Check if this is a sample game before deleting
      const { data: gameData } = await supabase
        .from('games')
        .select('is_sample')
        .eq('id', gameId)
        .single();

      // Soft-delete: set end_date to now and mark as archived via a far-past end_date
      // Using a sentinel end_date of epoch to signal "archived/hidden"
      const { error } = await supabase
        .from('games')
        .update({ end_date: '1970-01-01T00:00:00.000Z' })
        .eq('id', gameId);

      if (error) throw error;

      // Prevent sample game from being recreated
      if (gameData?.is_sample) {
        localStorage.setItem('sample_game_dismissed', 'true');
      }

      setIsDeleteDialogOpen(false);
      toast({
        title: "Game Removed",
        description: "The game has been removed."
      });
      navigate("/games");
    } catch (error) {
      console.error("Error archiving game:", error);
      toast({
        title: "Error",
        description: "Failed to remove the game. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  if (isLoading || !game) {
    return (
      <div className="min-h-screen pb-16">
        <Header title="Edit Game" />
        <LoadingView />
        <NavBar />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16">
      <Header title="Edit Game" />

      <div className="px-4 pb-20 space-y-6">
        <EditGameForm
          game={game}
          onCancel={() => navigate(`/games/${gameId}`)}
          onSuccess={() => navigate(`/games/${gameId}`)}
        />

        {/* Danger Zone — only visible to game creator */}
        {isCreator && (
          <div className="border border-red-200 rounded-lg p-4 space-y-4">
            <h3 className="text-sm font-semibold text-red-600 uppercase tracking-wide">
              Danger Zone
            </h3>

            {/* End Game */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">End Game</p>
                <p className="text-xs text-muted-foreground">Mark this year's game as completed</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEndGameDialogOpen(true)}
                disabled={game.isArchived}
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                <Flag className="mr-1.5 h-3.5 w-3.5" />
                {game.isArchived ? "Already Ended" : "End Game"}
              </Button>
            </div>

            {/* Delete Game */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Delete Game</p>
                <p className="text-xs text-muted-foreground">Remove this game from your games list</p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Delete
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* End Game Confirmation Dialog */}
      <AlertDialog open={isEndGameDialogOpen} onOpenChange={setIsEndGameDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End Game</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to end this game? This will mark the current year as completed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isActionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleEndGame} disabled={isActionLoading}>
              {isActionLoading ? "Ending..." : "End Game"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Game</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this game? It will be removed from your games list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isActionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" disabled={isActionLoading}>
              {isActionLoading ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NavBar />
    </div>
  );
};

export default GameEdit;
